// The screens' model over the data layer's Store (src/data/store.ts): a thin adapter. The Store does the rules, the
// writes and Undo; this adds what the window needs besides (who is signed in, Pro, the connection, the to-dos that
// linger a moment after they are ticked) and turns the screens' multi-record actions into one step of Undo
// (`transaction`). The real app passes the store of the signed-in `Session`; the demo a `memoryStore`.

import { currentZone } from "../core/days";
import { accountMarks, suggestion } from "../core/featureLevel";
import type { MoveDestination } from "../core/actions";
import type { ArchiveFilter, Lists, Route } from "../core/lists";
import { parse } from "../core/parser";
import type { Area, Favorite, Habit, HabitSchedule, Project, Section, Share, Todo } from "../core/records";
import type { HabitPeriodTick, HabitProgress, LateHabitTick } from "../core/habits";
import { completedByName } from "../core/sharing";
import type { TemplateWhen } from "../core/templates";
import type { Day, Start } from "../core/types";
import type { MailAddress, MailService } from "../data/mail";
import type { SharingActions } from "../data/shareService";
import type { Store, StoreState } from "../data/store";
import type { SyncStatus } from "../core/syncStatus";
import { prefs, subscribePrefs } from "./prefs";
import type { AccountRecords, AuthActions, Model, ModelState, QuickEntry, SharedInfo, User } from "./model";

/** How long a just-completed to-do stays in its list, so a slip can be seen and taken back: 1.5 s, as in the kit
 *  (Store.toggleCompletion), long enough for the moment of ticking to play out (docs/59, 1b). */
export const LINGER_MS = 1500;

export interface StoreModelOptions {
  store: Store;
  auth: AuthActions;
  /** The signed-in person; null while signed out (the sign-in screen shows). */
  user: User | null;
  /** The person's Email to Todolix addresses (mailFirestore.ts), or addresses in memory for the demo. */
  mail: MailService;
  /** The server's `share` function (shareService.ts), or its stand-in in memory for the demo. */
  sharing: SharingActions;
  /**
   * The demo's cloud (`&syncBehind=09:17`, docs/64): shown whatever the sync says; Try Again then says it is still not
   * reachable, unless `recovers`.
   */
  simulatedSync?: { status: SyncStatus; recovers: boolean };
}

export class StoreModel implements Model {
  readonly store: Store;
  readonly auth: AuthActions;
  readonly sharing: SharingActions;
  private user: User | null;
  readonly mail: MailService;
  private mailAddresses: MailAddress[] = [];
  private readonly stopMail: () => void;
  private readonly stopPrefs: () => void;
  private readonly lingering = new Set<string>();
  private simulatedSync: { status: SyncStatus; recovers: boolean } | undefined;
  private readonly listeners = new Set<() => void>();
  private state: ModelState;
  private storeState: StoreState;
  private unsubscribe: () => void;

  constructor(options: StoreModelOptions) {
    this.store = options.store;
    this.auth = options.auth;
    this.sharing = options.sharing;
    this.user = options.user;
    this.mail = options.mail;
    this.simulatedSync = options.simulatedSync;
    this.storeState = this.store.getState();
    this.state = this.makeState();
    this.unsubscribe = this.store.subscribe(() => this.changed());
    // After how many days a to-do is stale: the browser's setting (prefs.ts).
    const listSettings = () => ({ staleAfterDays: prefs().staleAfterDays, habitsInOwnGroup: prefs().habitsInOwnGroup, todayShowsEmptySections: prefs().todayShowsEmptySections });
    this.store.setListSettings(listSettings());
    this.stopPrefs = subscribePrefs(() => this.store.setListSettings(listSettings()));
    this.stopMail = this.mail.subscribe((addresses) => {
      this.mailAddresses = addresses;
      this.changed();
    });
  }

  /** Signed in or out (the session changed). */
  setUser(user: User | null): void {
    this.user = user;
    this.changed();
  }

  dispose(): void {
    this.unsubscribe();
    this.stopMail();
    this.stopPrefs();
  }

  getState = (): ModelState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private changed(): void {
    this.storeState = this.store.getState();
    this.state = this.makeState();
    for (const listener of [...this.listeners]) listener();
  }

  private makeState(): ModelState {
    const state = this.storeState;
    const account = state.account;
    const records: AccountRecords = {
      todos: account.todos, projects: account.projects, areas: account.areas, sections: account.sections,
      birthdays: account.birthdays, favorites: account.favorites ?? [], templates: account.templates, habits: account.habits,
      inboxTraces: account.inboxTraces,
    };
    const isPro = account.user.premiumActive === true;
    const lists: Lists = this.store.lists();
    // Remote Config's `feature_versions` isn't read on the web yet: the built-in table says which web version shows a level.
    const updateSuggestion = state.suggestsUpdate ? suggestion(accountMarks(account.user)) : undefined;
    const result: ModelState = {
      status: state.status,
      sync: this.simulatedSync?.status ?? state.sync,
      suggestsUpdate: state.suggestsUpdate,
      ...(updateSuggestion ? { updateSuggestion } : {}),
      user: this.user,
      isPro,
      records,
      lists,
      today: state.today,
      zone: currentZone(),
      invitations: account.invitations,
      mailAddresses: this.mailAddresses.filter((address) => address.active),
      undo: { canUndo: state.canUndo, canRedo: state.canRedo, ...(state.undoTitle ? { undoTitle: state.undoTitle } : {}), ...(state.redoTitle ? { redoTitle: state.redoTitle } : {}) },
    };
    return result;
  }

  // MARK: To-dos

  private add(entry: QuickEntry): Todo | null {
    const options: Parameters<Store["quickAdd"]>[2] = {};
    if (entry.sectionID !== undefined) options.sectionID = entry.sectionID;
    if (entry.after !== undefined) options.after = entry.after;
    if (entry.ignoring) options.ignoring = entry.ignoring;
    if (entry.draft?.notes) options.notes = entry.draft.notes;
    const todo = this.store.quickAdd(entry.text, entry.route, options);
    if (!todo) return todo;
    const context = this.store.parserContext();
    if (entry.ignoring) context.ignoring = entry.ignoring;
    const parsed = parse(entry.text, context);
    const chosen = entry.draft;
    if (chosen) {
      // Values chosen in the card before saving; what the parser read in the words wins, as the words came last.
      const read = new Set(parsed.tokens.map((token) => token.kind));
      const next: Todo = { ...todo };
      if (chosen.checklist?.length) next.checklist = chosen.checklist;
      if (chosen.start && !read.has("start") && !read.has("repeats")) next.start = chosen.start;
      if (chosen.deadline && !read.has("deadline")) next.deadline = chosen.deadline;
      if (chosen.tags?.length) next.tags = [...new Set([...todo.tags, ...chosen.tags])];
      if (chosen.reminder && !read.has("time")) next.reminder = chosen.reminder;
      if (chosen.repeats && !read.has("repeats") && !read.has("habit")) next.repeats = chosen.repeats;
      this.store.updateTodo(next, "To-do added");
    }
    // "3 times a week" in the words: the new to-do becomes a habit with that How Often (docs/63 question 9), as with
    // the flame. A title of nothing but those words stays as typed.
    const habit = parsed.title === "" ? undefined : parsed.tokens.find((token) => token.kind === "habit");
    if (habit) this.store.makeHabitFrom(todo.id, habit.value);
    return this.store.todo(todo.id) ?? todo;
  }

  create = (entry: QuickEntry): Todo | null => {
    if (entry.text.trim() === "") return null;
    return this.store.transaction("To-do added", () => this.add(entry));
  };

  createMany = (entries: readonly QuickEntry[]): Todo[] =>
    this.store.transaction(entries.length === 1 ? "To-do added" : "To-dos added", () => {
      // Each goes below the one before, so the list reads in the order pasted.
      const created: Todo[] = [];
      for (const entry of entries) {
        if (entry.text.trim() === "") continue;
        const previous = created.at(-1);
        const todo = this.add(previous ? { ...entry, after: previous.id } : entry);
        if (todo) created.push(todo);
      }
      return created;
    });

  update = (todo: Todo, title?: string): void => {
    this.store.updateTodo(todo, title);
  };

  updateMany = (todos: readonly Todo[], title?: string): void => {
    this.store.updateTodos(todos, title);
  };

  toggleCompletion = (ids: readonly string[]): void => {
    const todos = ids.flatMap((id) => this.store.todo(id) ?? []);
    // Several at once: if any is open, all are completed; else all open again.
    const completing = todos.some((todo) => todo.completedAt === undefined);
    const chosen = todos.filter((todo) => (todo.completedAt === undefined) === completing);
    if (completing) for (const todo of chosen) this.lingering.add(todo.id);
    else for (const todo of chosen) this.lingering.delete(todo.id);
    this.store.setListSettings({ lingering: new Set(this.lingering) });
    this.store.transaction(completing ? "Completed" : "Reopened", () => {
      for (const todo of chosen) this.store.toggleCompletion(todo.id);
    });
    if (completing) {
      setTimeout(() => {
        for (const todo of chosen) this.lingering.delete(todo.id);
        this.store.setListSettings({ lingering: new Set(this.lingering) });
      }, LINGER_MS);
    }
  };

  delete = (ids: readonly string[]): void => {
    this.store.deleteTodos(ids);
  };

  move = (ids: readonly string[], destination: MoveDestination): void => {
    this.store.moveTodos(ids, destination);
  };

  schedule = (ids: readonly string[], start: Start): void => {
    this.store.schedule(ids, start);
  };

  moveToSection = (ids: readonly string[], sectionID: string | undefined, inToday: boolean): void => {
    this.store.transaction("Moved", () => {
      for (const id of ids) {
        if (inToday) this.store.moveToTodaySection(id, sectionID);
        else this.store.moveToSection(id, sectionID);
      }
    });
  };

  moveToSectionEnd = (ids: readonly string[], sectionID: string | undefined, shown: readonly string[], inToday: boolean): void => {
    this.store.moveToSectionEnd(ids, sectionID, shown, inToday);
  };

  moveToSectionTop = (ids: readonly string[], sectionID: string | undefined, shown: readonly string[], inToday: boolean): void => {
    this.store.moveToSectionTop(ids, sectionID, shown, inToday);
  };

  reorder = (ids: readonly string[], targetID: string, place: "before" | "after", inToday: boolean): void => {
    const moving = ids.filter((id) => id !== targetID).flatMap((id) => this.store.todo(id) ?? []).sort((a, b) => a.order - b.order);
    this.store.transaction("Moved", () => {
      // One after another, each right below the one before, so the group keeps its own order.
      let anchor = targetID;
      let where = place;
      for (const todo of moving) {
        this.store.moveNextTo(todo.id, anchor, where, inToday);
        anchor = todo.id;
        where = "after";
      }
    });
  };

  skip = (id: string): void => {
    this.store.skip(id);
  };

  keep = (id: string, start?: Start): void => {
    this.store.keep(id, start);
  };

  convertToProject = (id: string, areaID: string): Project | null => this.store.convertToProject(id, areaID);

  // MARK: Lists

  setArchiveFilter(filter: ArchiveFilter | undefined): void {
    this.store.setListSettings({ archiveFilter: filter });
  }

  leftToday(): void {
    this.store.leftToday();
  }

  // MARK: Projects, areas, sections

  addProject = (title: string, areaID: string, kind: "project" | "list"): Project => {
    const project = this.store.addProject(title, areaID, kind);
    if (!project) throw new Error("The project could not be added.");
    return project;
  };

  addSection = (title: string, projectID: string | undefined): Section => {
    const section = this.store.addSection(title, projectID);
    if (!section) throw new Error("The section could not be added.");
    return section;
  };

  updateSection = (section: Section): void => {
    this.store.updateSection(section);
  };

  deleteSection = (id: string): void => {
    this.store.deleteSection(id);
  };

  updateProject = (project: Project): void => {
    this.store.updateProject(project);
  };

  updateArea = (area: Area): void => {
    this.store.updateArea(area);
  };

  moveProject = (id: string, before: string | undefined, areaID: string): void => {
    this.store.moveProject(id, before, areaID);
  };

  moveArea = (id: string, before: string | undefined): void => {
    this.store.moveArea(id, before);
  };

  moveSection = (id: string, before: string | undefined): void => {
    this.store.moveSection(id, before);
  };

  setFavorites = (favorites: readonly Favorite[]): void => {
    this.store.setFavorites([...favorites]);
  };

  // MARK: Sharing

  shareOf = (route: Route): SharedInfo | null => {
    const record = route.kind === "project" ? this.store.project(route.id) : route.kind === "area" ? this.store.area(route.id) : undefined;
    if (!record) return null;
    const share = this.store.shareOf(route.kind === "project" ? "projects" : "areas", record);
    return share ? this.sharedInfo(share) : null;
  };

  completedByName = (todo: Todo): string | undefined => {
    if (todo.completedBy === undefined || todo.completedBy === this.store.uid) return undefined;
    return completedByName(todo, this.store.shareOf("todos", todo));
  };

  private sharedInfo(share: Share): SharedInfo {
    const people = Object.entries(share.members).map(([uid, member]) => ({
      uid, name: member.name, email: member.email, isOwner: uid === share.ownerUID, isYou: uid === this.store.uid,
    }));
    people.sort((a, b) => (a.isOwner !== b.isOwner ? (a.isOwner ? -1 : 1) : a.name.localeCompare(b.name, undefined, { sensitivity: "base" })));
    return { id: share.id, kind: share.kind, title: share.title, youOwn: share.ownerUID === this.store.uid, people };
  }

  // MARK: Templates

  useTemplate = (templateID: string, title: string, areaID: string | undefined, when: TemplateWhen): Project | null =>
    this.store.useTemplate(templateID, title, areaID, when);

  // MARK: Habits

  habitProgress = (habit: Habit): HabitProgress => this.store.habitProgress(habit);

  habitDoneDays = (habitID: string): ReadonlySet<string> => this.store.habitDoneDays(habitID);

  habitPeriodTick = (todoID: string): HabitPeriodTick | undefined => this.store.habitPeriodTick(todoID);

  habitCardDay = (habitID: string): Todo | undefined => this.store.habitCardDay(habitID);

  isUnstoredHabitDay = (todo: Todo): boolean => this.store.isUnstoredHabitDay(todo);

  countHabitToday = (day: Todo): void => {
    this.store.countHabitToday(day);
  };

  finishHabitCard = (todo: Todo): void => {
    this.store.finishHabitCard(todo);
  };

  addHabit = (title: string, options?: { areaID?: string; schedule?: HabitSchedule; notes?: string; neverMissTwice?: boolean }): Habit | null => {
    const chosen = { ...options };
    if (chosen.neverMissTwice === undefined && !prefs().neverMissTwiceDefault) chosen.neverMissTwice = false;
    return this.store.addHabit(title, chosen);
  };

  updateHabit = (habit: Habit, title?: string): void => {
    this.store.updateHabit(habit, title);
  };

  makeHabit = (todoID: string): Habit | null => this.store.makeHabitFrom(todoID);

  turnIntoTodo = (habitID: string): string | undefined => this.store.turnIntoTodo(habitID);

  deleteHabit = (habitID: string): void => {
    this.store.deleteHabit(habitID);
  };

  pauseHabit = (habitID: string, until: Day): void => {
    this.store.pauseHabit(habitID, until);
  };

  resumeHabit = (habitID: string): void => {
    this.store.resumeHabit(habitID);
  };

  canRestHabitToday = (habitID: string): boolean => this.store.canRestHabitToday(habitID);

  restHabitToday = (habitID: string): void => {
    this.store.restHabitToday(habitID);
  };

  restsHabitToday = (habitID: string): boolean => this.store.restsHabitToday(habitID);

  unrestHabitToday = (habitID: string): void => {
    this.store.unrestHabitToday(habitID);
  };

  setHabitDay = (habitID: string, day: Day, done: boolean): void => {
    this.store.setHabitDay(habitID, day, done);
  };

  lateHabitTick = (todo: Todo): LateHabitTick | undefined => this.store.lateHabitTick(todo);

  countForYesterday = (late: LateHabitTick): void => {
    this.store.countForYesterday(late);
  };

  newHabitMilestone = (habitID: string): number | undefined => (prefs().celebratesMilestones ? this.store.newHabitMilestone(habitID) : undefined);

  noteMilestoneShown = (habitID: string, milestone: number): void => {
    this.store.noteMilestoneShown(habitID, milestone);
  };

  moveHabit = (habitID: string, before: string | undefined): void => {
    this.store.moveHabit(habitID, before);
  };

  moveHabitAfter = (habitID: string, target: string): void => {
    this.store.moveHabitAfter(habitID, target);
  };

  setHabitsInOwnGroup = (on: boolean): void => {
    this.store.setListSettings({ habitsInOwnGroup: on });
  };

  // MARK: Undo

  undo = (): void => {
    this.store.undo();
  };

  redo = (): void => {
    this.store.redo();
  };

  // MARK: Account

  deleteAllData = async (): Promise<void> => {
    const account = this.store.getState().account;
    this.store.transaction("All data deleted", () => {
      this.store.deleteTodos(account.todos.map((todo) => todo.id));
      for (const section of account.sections) this.store.deleteSection(section.id);
      for (const project of account.projects) this.store.deleteProject(project.id);
      for (const area of account.areas) this.store.deleteArea(area.id);
    });
  };

  // MARK: Sync

  retrySync = async (): Promise<boolean> => {
    const simulated = this.simulatedSync;
    if (simulated) {
      await new Promise((done) => setTimeout(done, 1000));
      if (!simulated.recovers) return false;
      this.simulatedSync = undefined;
      this.changed();
      return true;
    }
    return this.store.retrySync();
  };
}
